<template>
  <div class="row">
    <div class="col-12">
      <!-- Export Buttons -->
      <table-export-buttons
        v-if="certifications?.length"
        :columns="tableColumns"
        :data="sortedCertifications"
        file-name="certifications-report"
        title="Certifications Report"
        class="mb-3"
      />

      <div class="table-responsive">
        <table
          v-if="certifications?.length"
          :id="id"
          class="table table-hover table-custom"
        >
          <thead class="text-nowrap">
            <tr>
              <th @click="sortBy('isExpired')" class="cursor-pointer">
                <i :class="getSortIcon('isExpired')"></i>
              </th>
              <th @click="sortBy('certificate')" class="cursor-pointer">
                Certificate <i :class="getSortIcon('certificate')"></i>
              </th>
              <th
                v-if="showVesselNames"
                @click="sortBy('vessel_name')"
                class="cursor-pointer"
              >
                Vessel <i :class="getSortIcon('vessel_name')"></i>
              </th>
              <th @click="sortBy('issued_by')" class="cursor-pointer">
                Issued By <i :class="getSortIcon('issued_by')"></i>
              </th>
              <th @click="sortBy('issue_date')" class="cursor-pointer">
                Issue Date <i :class="getSortIcon('issue_date')"></i>
              </th>
              <th @click="sortBy('expiry_date')" class="cursor-pointer">
                Expiry Date <i :class="getSortIcon('expiry_date')"></i>
              </th>
              <th @click="sortBy('last_endorsed')" class="cursor-pointer">
                Last Endorsed <i :class="getSortIcon('last_endorsed')"></i>
              </th>
              <th @click="sortBy('due_open')" class="cursor-pointer">
                Due Open <i :class="getSortIcon('due_open')"></i>
              </th>
              <th @click="sortBy('due_close')" class="cursor-pointer">
                Due Close <i :class="getSortIcon('due_close')"></i>
              </th>
              <th @click="sortBy('endorsed_by')" class="cursor-pointer">
                Endorsed By <i :class="getSortIcon('endorsed_by')"></i>
              </th>
              <th @click="sortBy('remarks')" class="cursor-pointer">
                Remarks <i :class="getSortIcon('remarks')"></i>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(cert, i) in sortedCertifications"
              :key="cert.id || i"
              class="cursor-pointer"
              @click="openForViewByCert(cert)"
            >
              <!-- Table view -->
              <td
                :data-order="cert.isExpired ?? '0'"
                :title="getExpiryLevelText(cert.isExpired)"
              >
                <i
                  v-if="cert.isExpired"
                  :class="`${getExpiryLevelColorClass(
                    cert.isExpired
                  )} ri-error-warning-line`"
                />
              </td>
              <td>{{ cert.certificate }}</td>
              <td v-if="showVesselNames">{{ cert.vessel_name }}</td>
              <td>{{ cert.issued_by }}</td>
              <td
                :class="{
                  'border border-2 border-danger text-danger': !cert.issue_date,
                }"
              >
                {{
                  cert.issue_date
                    ? moment(cert.issue_date).format("DD-MM-YYYY")
                    : "Missing Issue Date!"
                }}
              </td>
              <td>
                {{
                  cert.expiry_date
                    ? moment(cert.expiry_date).format("DD-MM-YYYY")
                    : ""
                }}
              </td>
              <td>
                {{
                  cert.last_endorsed
                    ? moment(cert.last_endorsed).format("DD-MM-YYYY")
                    : ""
                }}
              </td>
              <td>
                {{
                  cert.due_open
                    ? moment(cert.due_open).format("DD-MM-YYYY")
                    : ""
                }}
              </td>
              <td>
                {{
                  cert.due_close
                    ? moment(cert.due_close).format("DD-MM-YYYY")
                    : ""
                }}
              </td>
              <td>{{ getUserName(cert.endorsed_by) }}</td>
              <td>{{ truncate(cert.remarks, 25) }}</td>

              <td>
                <button
                  v-if="cert.documents > 0"
                  class="btn btn-sm btn-primary"
                  title="Download Certificate"
                  type="button"
                  @click.stop="openDocumentDownloaderByCert(cert)"
                >
                  <i class="fa fa-download" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import CertificationEditorModal from "./CertificationEditorModal.vue";
import CertificationDownloaderModal from "./CertificationDownloaderModal.vue";
import TableExportButtons from "@/components/tableExport/TableExportButtons.vue";
import moment from "moment";
import { getVessel } from "@/apis/vessels";
import { getDocuments } from "@/apis/files";
import {
  getExpiryLevelColorClass,
  getExpiryLevelText,
} from "@/helpers/certificates";

export default {
  name: "CertificationsTable",
  components: {
    TableExportButtons,
  },
  props: {
    id: {
      type: String,
      default: "datatable",
    },
    certifications: {
      type: Array,
      default: () => [],
    },
    certificationDocuments: {
      type: Array,
      default: () => [],
    },
    showVesselNames: {
      type: Boolean,
      default: false,
    },
    allowEdit: {
      type: Boolean,
      default: true,
    },
    allowDelete: {
      type: Boolean,
      default: true,
    },
    projectOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      moment,
      vesselsData: {},
      sortKey: "isExpired",
      sortOrder: "desc",
    };
  },
  computed: {
    tableColumns() {
      const cols = [
        {
          key: "isExpired",
          label: "Status",
          formatter: (val) => (val ? "Expired" : "Valid"),
        },
        { key: "certificate", label: "Certificate" },
      ];

      if (this.showVesselNames) {
        cols.push({ key: "vessel_name", label: "Vessel" });
      }

      cols.push(
        { key: "issued_by", label: "Issued By" },
        {
          key: "issue_date",
          label: "Issue Date",
          formatter: (val) => (val ? moment(val).format("DD-MM-YYYY") : ""),
        },
        {
          key: "expiry_date",
          label: "Expiry Date",
          formatter: (val) => (val ? moment(val).format("DD-MM-YYYY") : ""),
        },
        {
          key: "last_endorsed",
          label: "Last Endorsed",
          formatter: (val) => (val ? moment(val).format("DD-MM-YYYY") : ""),
        },
        {
          key: "due_open",
          label: "Due Open",
          formatter: (val) => (val ? moment(val).format("DD-MM-YYYY") : ""),
        },
        {
          key: "due_close",
          label: "Due Close",
          formatter: (val) => (val ? moment(val).format("DD-MM-YYYY") : ""),
        },
        {
          key: "endorsed_by",
          label: "Endorsed By",
          formatter: (val) => this.getUserName(val),
        },
        { key: "remarks", label: "Remarks" }
      );

      return cols;
    },
    sortedCertifications() {
      if (!this.certifications?.length) return [];
      if (!this.sortKey) return this.certifications;

      const dateFields = [
        "issue_date",
        "expiry_date",
        "last_endorsed",
        "due_open",
        "due_close",
      ];

      return [...this.certifications].sort((a, b) => {
        let valA = a[this.sortKey];
        let valB = b[this.sortKey];

        // Handle Date Sorting
        if (dateFields.includes(this.sortKey)) {
          const timeA = this.parseDate(valA);
          const timeB = this.parseDate(valB);

          if (timeA === timeB) return 0;
          return this.sortOrder === "asc" ? timeA - timeB : timeB - timeA;
        }

        // Handle Regular Fields (Strings / Numbers / Booleans)
        valA = valA ?? "";
        valB = valB ?? "";

        if (valA < valB) return this.sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return this.sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    },
  },
  async mounted() {
    const routeVesselId = this.$route.params.vesselId;
    if (routeVesselId) {
      await this.fetchVessel(routeVesselId);
    }
  },
  methods: {
    getExpiryLevelColorClass,
    getExpiryLevelText,
    async fetchVessel(vesselId) {
      if (!vesselId) return null;

      if (this.vesselsData[vesselId]) {
        return this.vesselsData[vesselId];
      }

      try {
        const response = await getVessel(vesselId);
        const data = response?.data || null;

        if (data) {
          this.$set(this.vesselsData, vesselId, data);
        }
        return data;
      } catch (error) {
        console.error(`Failed to fetch vessel ${vesselId}:`, error);
        return null;
      }
    },
    getUserName(id) {
      if (!id) return "-";
      const usersList = this.$store.state.defaultStore.users || [];
      const foundUser = usersList.find((user) => user.id === id);
      return foundUser ? foundUser.name : "-";
    },

    parseDate(val) {
      if (!val) return 0;
      if (typeof val === "string" && /^\d{2}-\d{2}-\d{4}$/.test(val)) {
        const [day, month, year] = val.split("-");
        return new Date(`${year}-${month}-${day}`).getTime();
      }
      const timestamp = new Date(val).getTime();
      return isNaN(timestamp) ? 0 : timestamp;
    },

    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
    },

    getSortIcon(key) {
      if (this.sortKey !== key) return "fa fa-sort text-muted opacity-50 ms-1";
      return this.sortOrder === "asc"
        ? "fa fa-sort-up text-primary ms-1"
        : "fa fa-sort-down text-primary ms-1";
    },

    async openForView(index) {
      return await this.openForEdit(index, false);
    },

    openForViewByCert(cert) {
      const originalIndex = this.certifications.indexOf(cert);
      return this.openForEdit(
        originalIndex !== -1 ? originalIndex : cert,
        false
      );
    },

    openDocumentDownloaderByCert(cert) {
      const originalIndex = this.certifications.indexOf(cert);
      return this.documentDownloader(
        originalIndex !== -1 ? originalIndex : cert
      );
    },

    async openForEdit(index, startAsEditable = true) {
      const field = this.certifications[index];

      await this.fetchVessel(field?.vessel_id);
      const params = {
        certification: field,
        certificationDocuments: await this.getCertificationDocumentsForCert(
          field
        ),
        projectOptions: this.projectOptions,
        allowEdit: this.allowEdit,
        startAsEditable,
        vesselData: this.vesselsData[field?.vessel_id] || null,
        allowDelete: this.allowDelete,
        onDeleteCertification: (certification) => {
          this.$emit("removeCertificationByIndex", certification);
        },
        onSupersedeCertification: (certification) => {
          this.$emit("updateCertificate", certification);
        },
      };

      this.$modal.show(
        CertificationEditorModal,
        params,
        {
          name: "certification-editor-modal",
          height: "auto",
          width: "80%",
          scrollable: true,
        },
        {
          "before-close": async (e) => {
            const newValue = e.params?.value;
            if (!newValue) return;
            this.$emit("updateCertificate", newValue);
          },
        }
      );
    },

    async documentDownloader(index) {
      const field =
        typeof index === "number" ? this.certifications[index] : index;
      const params = {
        certification: field,
        certificationDocuments: await this.getCertificationDocumentsForCert(
          field
        ),
      };
      this.$modal.show(
        CertificationDownloaderModal,
        params,
        {
          name: "CertificationDownloaderModal",
          height: "auto",
          width: "80%",
          scrollable: true,
        },
        {
          "before-close": async () => {},
        }
      );
    },

    async getCertificationDocumentsForCert(cert) {
      const certId = cert?.id || cert?.certification_id;
      const documents = this.certificationDocuments?.filter(
        (doc) => doc.indexOf(`/certifications/${certId}/`) > -1
      );
      if (documents && documents.length > 0) {
        return documents;
      }

      const downloadedDocs = await getDocuments(
        `vessels/${cert?.vessel_id}/certifications`,
        cert?.vessel_id
      );

      return downloadedDocs?.data?.filter(
        (doc) => doc.indexOf(`/certifications/${certId}/`) > -1
      );
    },

    truncate(text, len) {
      if (!text) return text;
      if (len < 3) return text;
      return text.length <= len ? text : text.substring(0, len - 3) + "...";
    },

    isInvalid() {
      return false;
    },
  },
};
</script>

<style scoped>
.table tr td .vdp-datepicker {
  white-space: normal;
}

.table-responsive {
  overflow-x: visible;
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}
</style>
